import { Callout, ICalloutProps, IRawStyle } from '@fluentui/react';
import { forwardRef, HTMLAttributes, ReactElement, Ref, UIEventHandler, useEffect, useState } from 'react';

import { IComponent } from '../../models/components.models';
import { calloutContentStylesNoPadding } from '../../themes/themes';
import { useClassNames } from '../../utils/hooks';
import { BasicList, IBasicListProps } from '../BasicList/BasicList';
import { basicListStylesSearch } from '../BasicList/BasicList.styles';
import { DetailsList, IDetailsListProps } from '../DetailsList/DetailsList';
import { detailsListStylesList } from '../DetailsList/DetailsList.styles';
import { EmptyMessage } from '../EmptyMessage/EmptyMessage';
import { IItemProps, Item } from '../Item/Item';
import { itemStylesSearch } from '../Item/Item.styles';
import { Loader } from '../Loader/Loader';
import { loaderStylesSpinnerHeight100, loaderStylesSpinnerXLarge } from '../Loader/Loader.styles';
import { ISearchBoxProps, SearchBox } from '../SearchBox/SearchBox';
import { searchBoxStylesSearch, searchBoxStylesSearchUnderline } from '../SearchBox/SearchBox.styles';
import { IWidgetItemProps, WidgetItem } from '../WidgetItem/WidgetItem';
import { widgetItemStylesList } from '../WidgetItem/WidgetItem.styles';
import { ISearchClassNames, ISearchStyles, searchStylesDefault } from './Search.styles';

export interface SearchResultItemType {
  id: string;
  name: string;
  count?: number;
  style?: IRawStyle;
  selected?: boolean;
}

export interface SearchResultItemFields {
  idField?: string;
  titleField?: string;
  descriptionField?: string;
  imagePathField?: string;
  iconNameField?: string;
  disabledField?: string;
}

export interface ISearchProps extends IComponent<ISearchStyles>, HTMLAttributes<HTMLDivElement> {
  calloutProps?: ICalloutProps;
  // searchBox props
  searchBoxProps?: ISearchBoxProps;
  placeholder?: string;
  defaultSearchText?: string;
  onSearchTextChange?: (text: string) => void;
  // basic list props
  basicListProps?: IBasicListProps<SearchResultItemType>;
  searchResultItemTypes?: SearchResultItemType[];
  // details list props
  detailsListProps?: Partial<IDetailsListProps>;
  searchResultItemFields?: SearchResultItemFields;
  searchResultItems?: any[];
  widgetItemProps?: IWidgetItemProps<any>;
  onClickItemType?: (item: SearchResultItemType) => void;
  onClickItem?: (item: any) => void;
  onDismissCallout?: () => void;
  onScrollDetailsList?: UIEventHandler<HTMLDivElement>;
  onRenderSearchResultItemActions?: (item: any) => JSX.Element;
  itemProps?: IItemProps<SearchResultItemType>;
  loadingMessage?: string;
  emptyMessage?: string;
  hasSearchResult?: boolean;
  minCalloutWidth?: number;
  actions?: ReactElement;
}

const getCalloutWidth = (searchBoxEl: HTMLDivElement | null, minWidth = 700) => {
  const width = searchBoxEl ? searchBoxEl.clientWidth + 2 : 0;
  return width > minWidth ? width : minWidth;
};

export const Search = forwardRef((props: ISearchProps, ref: Ref<HTMLDivElement>) => {
  const {
    styles,
    className,
    calloutProps: cProps,
    detailsListProps: dlProps,
    searchResultItemFields,
    searchResultItems,
    widgetItemProps: wiProps,
    onClickItemType,
    onClickItem,
    onDismissCallout,
    onScrollDetailsList,
    onRenderSearchResultItemActions,
    basicListProps: blProps,
    searchResultItemTypes,
    itemProps: iProps,
    searchBoxProps: sbProps,
    placeholder,
    defaultSearchText,
    loadingMessage,
    emptyMessage = 'No results found',
    onSearchTextChange,
    hasSearchResult,
    minCalloutWidth,
    actions,
    ...elementProps
  } = props;
  const [refSearchBox, setRefSearchBox] = useState<HTMLDivElement | null>(null);
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const classNames = useClassNames<Partial<ISearchStyles>, ISearchClassNames>(
    'Search',
    searchStylesDefault,
    styles,
    className
  );
  const searchBoxProps: ISearchBoxProps = {
    styles: searchBoxStylesSearch,
    placeholder,
    defaultValue: defaultSearchText,
    onChange: (_e, text = '') => onSearchTextChange!(text),
    ...sbProps,
  };
  const calloutProps: ICalloutProps = {
    target: refSearchBox,
    calloutWidth: getCalloutWidth(refSearchBox, minCalloutWidth),
    styles: calloutContentStylesNoPadding,
    gapSpace: -50,
    onDismiss: () => {
      setIsCalloutVisible(false);
      onDismissCallout && onDismissCallout();
    },
    ...cProps,
  };
  const itemProps: Partial<IItemProps<SearchResultItemType>> = {
    styles: itemStylesSearch,
    onClick: onClickItemType,
    ...iProps,
  };
  const basicListProps: IBasicListProps<SearchResultItemType> = {
    styles: basicListStylesSearch,
    horizontal: true,
    itemRenderer: (d: SearchResultItemType) => <Item {...itemProps} selected={d.selected} data={d} />,
    data: searchResultItemTypes,
    ...blProps,
  };
  const widgetItemProps = {
    styles: widgetItemStylesList,
    onClick: onClickItem,
    ...searchResultItemFields,
    ...wiProps,
  } as IWidgetItemProps<any>;
  const detailsListProps = {
    columns: [
      {
        key: 'widgetItem',
        name: 'Widget Item',
        minWidth: 200,
        onRender: (item) => (
          <WidgetItem {...widgetItemProps} actions={onRenderSearchResultItemActions!(item)} data={item} />
        ),
      },
    ],
    styles: detailsListStylesList,
    isHeaderVisible: false,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: -1,
    },
    items: searchResultItems,
    ...dlProps,
  } as IDetailsListProps;
  useEffect(() => {
    if (hasSearchResult && refSearchBox) {
      const handler = () => {
        setIsCalloutVisible(true);
      };
      refSearchBox.addEventListener('click', handler);
      return () => {
        refSearchBox.removeEventListener('click', handler);
      };
    }
  }, [hasSearchResult, refSearchBox]);
  return (
    <div {...elementProps} className={classNames.root} ref={ref} data-test={props['data-test'] || 'search'}>
      <SearchBox
        {...searchBoxProps}
        styles={{ root: { height: 32 } }}
        ref={setRefSearchBox}
        onFocus={hasSearchResult ? (e) => e.target.blur() : undefined}
      />
      {hasSearchResult && isCalloutVisible && (
        <Callout {...calloutProps}>
          <div className={classNames.calloutContent}>
            <div className={classNames.searchBoxContainer}>
              <SearchBox {...searchBoxProps} styles={searchBoxStylesSearchUnderline} />
            </div>
            {searchResultItemTypes && searchResultItemTypes.length > 0 && (
              <div className={classNames.itemTypeCount}>
                <BasicList {...basicListProps} />
              </div>
            )}
            {loadingMessage ? (
              <div className={classNames.loadingMessage}>
                <Loader styles={[loaderStylesSpinnerXLarge, loaderStylesSpinnerHeight100]} label={loadingMessage} />
              </div>
            ) : searchResultItems && searchResultItems.length > 0 ? (
              <>
                <div
                  className={classNames.items}
                  data-is-scrollable="true"
                  style={{ marginBottom: actions ? 0 : 16 }}
                  onScroll={onScrollDetailsList}
                >
                  <DetailsList {...detailsListProps} />
                </div>
                {actions && <div className={classNames.actions}>{actions}</div>}
              </>
            ) : (
              <div className={classNames.emptyMessage}>
                <EmptyMessage message={emptyMessage} />
              </div>
            )}
          </div>
        </Callout>
      )}
    </div>
  );
});
